import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, Lock, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getAssessmentPageCopy } from '@/data/assessmentPageCopy';
import { getApiBase } from '@/lib/apiBase';
import { setSessionToken } from '@/lib/flowStorage';
import { trackClientEvent } from '@/lib/trackClientEvent';
import { isUsableReportPrintHtml, printFullDocumentHtml } from '@/lib/printFullDocumentHtml';
import { trackEvent } from '@/lib/trackEvent';
import PageSeo from '@/seo/PageSeo';

const API_BASE = getApiBase();

export default function ReportPreviewPage() {
  const { token } = useParams();
  const printCopy = useMemo(() => getAssessmentPageCopy('en'), []);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState('');
  const [data, setData] = useState(null);
  const [printErr, setPrintErr] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      setErr('');
      try {
        const t = String(token || '').trim();
        if (!t) throw new Error('Invalid link.');
        const res = await fetch(`${API_BASE}/api/public/report-preview/${encodeURIComponent(t)}`);
        const d = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(d.error || 'Could not load preview.');
        if (!cancelled) {
          setData(d);
          setPrintErr('');
        }
        setSessionToken(t);
        await trackClientEvent('report_viewed', { meta: { kind: 'preview_page' } });
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const openClientPrintDialog = useCallback(async () => {
    setPrintErr('');
    const t = String(token || '').trim();
    let usedFullHtml = false;
    let paymentBlocked = false;
    if (t) {
      try {
        const res = await fetch(`${API_BASE}/api/public/report-print-html/${encodeURIComponent(t)}`);
        if (res.status === 402) {
          paymentBlocked = true;
          setPrintErr(printCopy.reportPreviewRequiresPayment);
        } else if (res.ok) {
          const html = await res.text();
          if (isUsableReportPrintHtml(html) && printFullDocumentHtml(html)) {
            usedFullHtml = true;
          }
        }
      } catch {
        /* ignore */
      }
    }
    void trackEvent('report_print_dialog_opened', {
      meta: {
        surface: 'report_preview_magic_link',
        mode: usedFullHtml ? 'full_html' : 'full_html_failed',
      },
    });
    if (!usedFullHtml && !paymentBlocked) {
      setPrintErr(printCopy.reportPreviewPrintUnavailable);
    }
  }, [token, printCopy]);

  const payHref = `/payment?token=${encodeURIComponent(String(token || '').trim())}`;

  const hasPdfCreds = Boolean(data?.pdfDownload?.reportId && data?.pdfDownload?.token);

  return (
    <div className="relative min-h-[100dvh] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-10 text-white">
      <PageSeo title="Report preview" description="Preview your career report." path="/report-preview" />
      <div className="mx-auto max-w-lg">
        {busy ? (
          <div className="flex flex-col items-center justify-center gap-3 py-24">
            <Loader2 className="h-10 w-10 animate-spin text-teal-300" />
            <p className="text-sm text-white/70">Loading preview…</p>
          </div>
        ) : err ? (
          <p className="rounded-xl border border-red-500/30 bg-red-950/40 p-4 text-sm text-red-100">{err}</p>
        ) : (
          <div className="crc-report-print-root space-y-6">
            <div className="crc-print-preserve-bg crc-print-avoid-break rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-md">
              <p className="text-xs font-semibold uppercase tracking-widest text-teal-300/90">Career snapshot</p>
              <h1 className="mt-2 text-2xl font-bold tracking-tight">Hi {data?.firstName || 'there'}</h1>
              <p className="mt-3 text-sm leading-relaxed text-white/80">{data?.tagline}</p>
            </div>

            <div className="crc-print-preserve-bg crc-print-avoid-break relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-md">
              <div
                className={`space-y-3 ${data?.paymentRequired ? 'pointer-events-none select-none blur-sm' : ''}`}
                aria-hidden={data?.paymentRequired || undefined}
              >
                {data?.topTraits?.length ? (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {data.topTraits.map((k) => (
                      <div key={k} className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 capitalize">
                        {k}{' '}
                        <span className="font-mono text-teal-200">
                          {data?.profilePreview?.[k] != null ? `${data.profilePreview[k]}%` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
                <p className="text-xs text-white/50">Full personalised sections unlock after payment.</p>
              </div>
              {data?.paymentRequired ? (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-900/10">
                  <Lock className="h-10 w-10 text-white/40" aria-hidden />
                </div>
              ) : null}
            </div>

            {data?.paymentRequired ? (
              <Button
                asChild
                className="crc-no-print w-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 py-6 text-base font-semibold text-slate-900 shadow-lg hover:from-amber-400 hover:to-orange-400"
              >
                <Link to={payHref}>Unlock full report</Link>
              </Button>
            ) : (
              <div className="space-y-4">
                <p className="crc-no-print text-center text-sm leading-relaxed text-emerald-200/95">
                  {printCopy.reportPreviewPaidUsePrint}
                </p>
                {printErr ? (
                  <p className="crc-no-print rounded-lg border border-amber-400/40 bg-amber-950/30 px-3 py-2 text-center text-sm text-amber-100">
                    {printErr}
                  </p>
                ) : null}
                {!hasPdfCreds ? (
                  <p className="crc-no-print text-center text-xs text-amber-200/90">
                    Full PDF credentials are not on file yet. Please contact support with your payment confirmation.
                  </p>
                ) : null}
                <div className="crc-no-print flex flex-col gap-2">
                  <Button
                    type="button"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-teal-500 to-sky-500 py-6 text-base font-semibold text-white shadow-lg hover:from-teal-400 hover:to-sky-400"
                    onClick={() => void openClientPrintDialog()}
                  >
                    <Printer className="h-5 w-5 shrink-0" aria-hidden />
                    {printCopy.printSavePdf}
                  </Button>
                  <p className="text-center text-xs text-white/60">{printCopy.printSavePdfHint}</p>
                </div>
              </div>
            )}

            <p className="crc-no-print text-center text-xs text-white/40">
              <Link className="underline-offset-2 hover:text-white hover:underline" to="/">
                Home
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
